import { useContext, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { CircularProgress, Button } from "@mui/material";
import { Context } from "../../context/Context";
import usePost from "../../hooks/usePosts";
import MarkdownRenderer from "../../components/MarkdownRenderer/MarkdownRenderer";
import "./singlePost.css";

export default function SinglePost() {
  const location = useLocation();
  const blog_id = location.pathname.split("/")[2];
  const token = localStorage.getItem("token");
  const { user } = useContext(Context);
  const navigate = useNavigate();

  const { post, setPost, loading, deletePost, updatePost } = usePost(
    blog_id,
    user,
    token
  );

  const [updateMode, setUpdateMode] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [photo, setPhoto] = useState("");

  // Font size zoom control
  const [fontSizeScale, setFontSizeScale] = useState(0);
  const [showMagnifier, setShowMagnifier] = useState(false);
  const baseFontSize = 16;
  const computedFontSize = `${baseFontSize + fontSizeScale * 2}px`;

  if (loading) {
    return (
      <div className="loaderWrapper">
        <CircularProgress size={50} />
      </div>
    );
  }

  if (!post) return <div className="error">Post not found.</div>;

  const isAuthor = user && post.author && user._id === post.author._id;

  const handleDelete = async () => {
    if (!window.confirm("Delete this post?")) return;
    await deletePost();
    navigate("/blogs");
  };

  const handleUpdate = async () => {
    if (desc.length <= 150) {
      window.alert(`⚠️ Your blog post is too short. Short posts may be automatically
          removed after one day. Consider adding more content.`)
    }
    await updatePost({ userId: user._id, title, desc, photo });
    console.log("It's working");
    setUpdateMode(false);
    navigate("/blogs");
  };

  return (
    <div className="singlePost">
      <div className="singlePostWrapper">
        {updateMode ? (
          <input
            type="text"
            value={photo}
            className="singlePostTitleInput"
            onChange={(e) => setPhoto(e.target.value)}
            placeholder="Image URL"
          />
        ) : (
          post.photo && (
            <img src={post.photo} alt="Post" className="singlePostImg" />
          )
        )}

        {updateMode ? (
          <input
            type="text"
            value={title}
            className="singlePostTitleInput"
            onChange={(e) => setTitle(e.target.value)}
          />
        ) : (
          <div className="singlePostTitle">
            <h2>{post.title}</h2>
            {isAuthor && (
              <div className="singlePostEdit">
                <i
                  className="singlePostIcon far fa-edit"
                  onClick={() => {
                    setTitle(post.title || "");
                    setDesc(post.desc || "");
                    setPhoto(post.photo || "");
                    setUpdateMode(true);
                  }}
                />
                <i
                  className="singlePostIcon far fa-trash-alt"
                  onClick={handleDelete}
                />
              </div>
            )}
          </div>
        )}

        <div className="singlePostInfo">
          <span className="singlePostAuthor">
            Author:{" "}
            {post.author ? (
              <Link to={`/user/${post.author._id}`} className="link">
                <b>{post.author.username.toUpperCase()}</b>
              </Link>
            ) : (
              <b>Unknown</b>
            )}
          </span>
          <span className="singlePostDate">
            {new Date(post.createdAt).toDateString()}
          </span>
          <span className="singlePostViews">👁️ {post.views || 0} views</span>
        </div>

        <div style={{ fontSize: computedFontSize }}>
          {updateMode ? (
            <textarea
              className="singlePostDescInput"
              rows="20"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              style={{ fontSize: computedFontSize }}
            />
          ) : (
            <div
              className="singlePostDesc"
              style={{ fontSize: computedFontSize }}
            >
              <MarkdownRenderer content={post.desc} />
            </div>
          )}
        </div>

        {updateMode && (
          <Button
            variant="contained"
            onClick={handleUpdate}
            sx={{
              backgroundColor: "#6a1b9a",
              mt: 2,
              alignSelf: "flex-end",
              "&:hover": { backgroundColor: "#4a148c" },
            }}
          >
            Update
          </Button>
        )}
      </div>
      <div className="fontSizeOverlay" title="Click to zoom in or out">
        <button
          className="magnifierToggle"
          onClick={() => setShowMagnifier(!showMagnifier)}
        >
          🔍
        </button>

        {showMagnifier && (
          <div className="magnifierControls">
            <button
              title="Zoom In"
              onClick={() => setFontSizeScale((prev) => Math.min(prev + 1, 2))}
            >
              A+
            </button>
            <button
              title="Zoom Out"
              onClick={() => setFontSizeScale((prev) => Math.max(prev - 1, -2))}
            >
              A-
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
